import { computeUtilization, parseRawAmount } from '../../../../utils/parsing'
import { exchangeRateAccumulator } from '../../accumulator'
import { LENDER_MODE_NO_MODE } from '../../../../assets'
import { createMarketUid } from '../../../../utils'
import { lenderShortName } from '../../../../utils/marketName'
import { CAction } from '../getters/types'

/**
 * Per-market display names, keyed `<chainId>:<cToken>` — NOT by bare address.
 * PAXO ships the SAME cToken addresses on XDC (50) and Linea (59144)
 * (`0x54ecc883…` is FXD on XDC and USDC on Linea), so a bare-address override
 * silently renames a market on another chain.
 *
 * Only for markets the generic `<lender> <symbol>` label CANNOT tell apart —
 * i.e. two markets of one fork sharing an underlying. The suffix is taken from
 * the cToken's OWN `name()`, never invented: labelling them v1/v2 would assert
 * a version relationship that does not exist, and these are purpose-scoped
 * markets, not generations.
 *
 * The pairs' unqualified halves are deliberately absent — their on-chain names
 * carry no qualifier either ("WEFI FXD", "EURS Vault"), so the generic label is
 * already correct for them.
 */
const COMPOUND_V2_NAME_OVERRIDES: Record<string, string> = {
  '1:0xccf4429db6322d5c611ee964527d42e5d685dd6a': 'Compound',

  // PAXO (WeFi) on XDC runs three assets twice, split by purpose. Verified
  // on-chain 2026-08-24 against each cToken's `name()` / `symbol()`.
  // vBuyFXD, against vFXD "WEFI FXD"
  '50:0xfc839b46019606b425f8b4c65b4479c3315de5c6': 'Paxo FXD Buy',
  // vBuyEURS, against vEURS "EURS Vault"
  '50:0xb7c3b74f897adce04db33d9438d60a8fcf70cbc1': 'Paxo EURS Buy',
  // vBorrowWXDC, against vWXDC "WEFI WXDC"
  '50:0xcc402f9573132b006d6b63b5d5b5a986767959a5': 'Paxo WXDC Borrow',
}
import {
  PRICE_DECIMALS,
  RESERVE_MANTISSA_DECIMALS,
  CTokenReader,
  ConvertEntryOpts,
  resolveMarketPrice,
} from './types'
import { DEFAULT_CLOSE_FACTOR, DEFAULT_LIQUIDATION_PENALTY } from './risk'
import { MarketPauseFlags } from '../pause'
import { cashLiquidity } from './liquidity'

export function convertSingleEntry(
  opts: ConvertEntryOpts & {
    reader: CTokenReader
    // global comptroller-derived values (standard path); when absent we fall
    // back to a per-market value (Venus) or the protocol default.
    closeFactor?: number
    liquidationPenalty?: number
    // per-market pause guardians read off the Comptroller, for the forks whose
    // lens omits them. Only the legs that came back are applied — an unknown
    // (reverted read) leaves the reader's own value alone.
    pause?: MarketPauseFlags
  },
) {
  const {
    entryRaw,
    reader,
    asset,
    chainId,
    lender,
    prices,
    additionalYields,
    cToken,
  } = opts

  const decs = asset.decimals as number
  const currentEntry = reader(entryRaw, asset)

  const borrowCollateralFactor = parseRawAmount(
    currentEntry?.collateralFactorMantissa?.toString(),
    RESERVE_MANTISSA_DECIMALS,
  )

  // Liquidation penalty (fraction): prefer a per-market liquidation incentive
  // mantissa when the reader exposes one (e.g. Venus), then the global
  // comptroller value threaded in from publicCallParse, then the default.
  const perMarketIncentive = (currentEntry as any)?.liquidationIncentiveMantissa
  let liquidationPenalty: number
  if (perMarketIncentive !== undefined && Number(perMarketIncentive) > 0) {
    liquidationPenalty = Math.max(
      Number(
        parseRawAmount(
          perMarketIncentive.toString(),
          RESERVE_MANTISSA_DECIMALS,
        ),
      ) - 1,
      0,
    )
  } else if (opts.liquidationPenalty !== undefined) {
    liquidationPenalty = opts.liquidationPenalty
  } else {
    liquidationPenalty = DEFAULT_LIQUIDATION_PENALTY
  }

  const closeFactor = opts.closeFactor ?? DEFAULT_CLOSE_FACTOR

  const exchangeRateRaw = currentEntry?.exchangeRateCurrent ?? 0
  const exchangeRateCurrent = parseRawAmount(
    exchangeRateRaw?.toString(),
    PRICE_DECIMALS,
  )

  const totalSupplyUnderlying =
    Number(parseRawAmount(currentEntry?.totalSupply?.toString(), decs)) *
    Number(exchangeRateCurrent)

  const totalDebt = Number(
    parseRawAmount(currentEntry?.totalBorrows?.toString(), decs),
  )

  const liquidity =
    cashLiquidity(currentEntry?.totalCash ?? currentEntry?.cash, decs) ??
    totalSupplyUnderlying - totalDebt
  const price = resolveMarketPrice(prices, asset, chainId)

  // @ts-ignore
  const pausedActions = { ...(currentEntry.pausedActions as any) }
  // Comptroller reads win over the reader's value where they exist: on the
  // plain-lens forks the reader has nothing but a hard-coded "not paused",
  // and on the lenses that DO publish pause state we never ask for them.
  if (opts.pause?.mintPaused !== undefined)
    pausedActions[CAction.MINT] = opts.pause.mintPaused
  if (opts.pause?.borrowPaused !== undefined)
    pausedActions[CAction.BORROW] = opts.pause.borrowPaused
  const allPaused =
    pausedActions[CAction.MINT] &&
    pausedActions[CAction.BORROW] &&
    pausedActions[CAction.REDEEM] &&
    pausedActions[CAction.REPAY]

  const poolId = asset.address
  const collateralActive = Boolean(currentEntry?.isListed)
  const borrowingEnabled =
    Boolean(currentEntry?.isBorrowAllowed) && !pausedActions[CAction.BORROW]

  const marketUid = createMarketUid(chainId, lender, cToken)

  const name =
    COMPOUND_V2_NAME_OVERRIDES[`${chainId}:${cToken.toLowerCase()}`] ??
    lenderShortName(lender) + ' ' + (asset?.symbol ?? '')

  return {
    marketUid,
    name,
    poolId,
    asset,
    // interest rates (use lender passed-in, not hardcoded)
    accumulator: exchangeRateAccumulator(exchangeRateRaw),
    depositRate: currentEntry.depositRate,
    variableBorrowRate: currentEntry.variableBorrowRate,
    stableBorrowRate: 0,
    totalLiquidity: liquidity,
    borrowLiquidity: liquidity,
    totalLiquidityUSD: liquidity * price,
    borrowLiquidityUSD: liquidity * price,
    totalDebt,
    totalDebtUSD: totalDebt * price,
    totalDeposits: totalSupplyUnderlying,
    totalDepositsUSD: totalSupplyUnderlying * price,
    totalDebtStable: 0,
    totalDebtStableUSD: 0,
    utilization: computeUtilization(totalSupplyUnderlying, totalDebt),
    intrinsicYield: additionalYields?.intrinsicYields?.[asset.assetGroup] ?? 0,
    config: {
      [LENDER_MODE_NO_MODE]: {
        category: LENDER_MODE_NO_MODE,
        // collateral factors
        borrowCollateralFactor: Number(borrowCollateralFactor),
        collateralFactor: Number(borrowCollateralFactor),
        borrowFactor: 1,
        liquidationPenalty,
        closeFactor,
        collateralDisabled: !collateralActive,
        debtDisabled: !borrowingEnabled,
      },
    },
    closeFactor,
    hasStable: false,
    underlying: asset.address,
    isActive: Boolean(currentEntry?.isListed) && !allPaused,
    isFrozen: pausedActions[CAction.MINT] && pausedActions[CAction.BORROW],
    rewards: [],
    collateralActive,
    borrowingEnabled,
    depositsEnabled: !pausedActions[CAction.MINT],
    params: {
      metadata: {
        cToken,
        exchangeRate: exchangeRateRaw.toString(),
        cTokenDecimals: Number(currentEntry?.cTokenDecimals?.toString()),
      },
    },
  }
}
